import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  CalendarDaysIcon,
  MapPinIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import { trackEvent } from '../lib/analytics';
import { track } from '../lib/track';

export default function ForParentsPage() {
  const faqs = [
    {
      question: "Is CampWhiz FREE?",
      answer: "Yes — searching, saving, and organizing schedules is FREE."
    },
    {
      question: "When do I pay?",
      answer: "Only if you want to sync your CampWhiz schedule with Google or iCal, or share it with other parents ($6/month or $60/year)."
    },
    {
      question: "Can I book camps directly through CampWhiz?",
      answer: "Not yet — you’ll find and organize camps here, then register on the camp’s site. Booking and scheduling are on the roadmap!"
    },
    {
      question: "Can multiple parents or caregivers access the same calendar?",
      answer: "Yes — with the paid plan, shared calendar syncing keeps everyone aligned."
    },
    {
      question: "Is my data safe?",
      answer: "Absolutely. Your family’s info stays private and secure — always."
    }
  ];

  return (
    <div className="page-container">
      <SEO 
        title="For Parents - One Hub for Camp Planning | CampWhiz"
        description="Finding camps shouldn't feel like a second job. CampWhiz brings everything together — real camps, verified details, and one shared place to stay organized. Find, sync, and track all your camp planning in one place."
        keywords="camp planning, summer camps, find camps, camp schedule, family calendar, camp search, parent resources"
        url="/for-parents"
      />
      
      <div className="page-content">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
              One Hub for Camp Planning
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
              Finding camps shouldn't feel like a second job.
            </p>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8">
              CampWhiz brings everything together — real camps, verified details, and one shared place to stay organized.
            </p>
            <div className="text-2xl font-semibold text-primary mb-8">
              Find. Sync. Track.
            </div>
            {/*
            <div className="mb-8 max-w-4xl mx-auto">
              <img 
                src="/images/for-parents-hero-visual.png" 
                alt="Relaxed parent beside pool vs. chaos of tabs and notes - showing how CampWhiz simplifies camp planning"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
            */}
          </div>

          {/* Find, Sync, Track Sections - Side by Side */}
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Find Section */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 flex flex-col min-h-[440px]">
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-primary text-white rounded-full w-14 h-14 flex items-center justify-center text-2xl font-bold">
                    1
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">
                  Find
                </h3>
                <p className="text-base text-gray-700 text-center mb-4">
                  CampWhiz brings every local camp into one clean, searchable map.
                </p>
                {/* TODO: Add final product video once ready */}
                {/*
                <div className="rounded-lg overflow-hidden mb-4 bg-gray-100">
                  <video
                    className="w-full h-auto"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                  >
                    <source src="/videos/for-parents-vid-1.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                */}
                <div className="mt-2 mb-4 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                <div className="space-y-4 flex-1 pb-6">
                  <div className="grid grid-cols-[1.5rem,1fr] gap-3 items-start">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-base">
                      <MagnifyingGlassIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Search by city, ZIP, or radius
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.5rem,1fr] gap-3 items-start">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-base">
                      <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Filter by age, activity, cost, or format
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.5rem,1fr] gap-3 items-start">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-base">
                      <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Explore AI-verified listings with accurate details
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.5rem,1fr] gap-3 items-start">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-base">
                      <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Discover community-based camps close to home
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 text-center mt-auto">
                  FREE for everyone.
                </p>
              </div>

              {/* Sync Section */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 flex flex-col min-h-[440px]">
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-primary text-white rounded-full w-14 h-14 flex items-center justify-center text-2xl font-bold">
                    2
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">
                  Sync
                </h3>
                <p className="text-base text-gray-700 text-center mb-4">
                  Once you’ve found the right camps, keep your plans organized in one place.
                </p>
                {/*
                <div className="bg-gray-100 rounded-lg p-3 mb-4 text-center text-sm text-gray-600 italic">
                  (Visual: clean family calendar UI with sync icons)
                </div>
                */}
                <div className="mt-2 mb-4 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                <div className="space-y-4 flex-1 pb-6">
                  <div className="grid grid-cols-[1.5rem,1fr] gap-3 items-start">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-base">
                      <CalendarDaysIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Create a CampWhiz schedule for your kids (FREE)
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.5rem,1fr] gap-3 items-start">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-base">
                      <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      View and organize each child's week (FREE)
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.5rem,1fr] gap-3 items-start">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-base">
                      <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Sync with Google Calendar or iCal, or share with other parents ($6/month or $60/year)
                    </p>
                  </div>
                </div>
                <p className="text-primary font-semibold text-sm text-center mt-auto">
                  Start FREE. Upgrade anytime.
                </p>
              </div>

              {/* Track Section */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 flex flex-col min-h-[440px]">
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-primary text-white rounded-full w-14 h-14 flex items-center justify-center text-2xl font-bold">
                    3
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">
                  Track
                </h3>
                <p className="text-base text-gray-700 text-center mb-4">
                  Stay organized as your plans come together.
                </p>
                {/*
                <div className="bg-gray-100 rounded-lg p-3 mb-4 text-center text-sm text-gray-600 italic">
                  (Visual: week view of color-coded kids’ schedules)
                </div>
                */}
                <div className="mt-2 mb-4 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                <div className="space-y-4 flex-1 pb-6">
                  <div className="grid grid-cols-[1.5rem,1fr] gap-3 items-start">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-base">
                      <CalendarDaysIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      View all saved camps and schedules in one place
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.5rem,1fr] gap-3 items-start">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-base">
                      <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Easily switch between kids’ calendars
                    </p>
                  </div>
                  <div className="grid grid-cols-[1.5rem,1fr] gap-3 items-start">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-base">
                      <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <p className="text-gray-900 text-base leading-relaxed">
                      Access everything from your phone, tablet, or laptop
                    </p>
                  </div>
                </div>
                <p className="text-center text-gray-600 text-sm mt-auto">
                  FREE features available for all users.
                </p>
              </div>
            </div>
          </div>

          {/* Why Parents Love It - Marketing Callout */}
          <div className="mb-16">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
              <div className="grid lg:grid-cols-2">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-purple-100/25 to-white/10 pointer-events-none z-0" />
                  <div className="relative z-10 h-full w-full flex items-center justify-center p-4 sm:p-5 backdrop-blur-sm">
                    <img
                      src="/images/for-parents-computer-cell.png"
                      alt="CampWhiz on desktop and mobile devices"
                      className="max-h-[360px] w-full object-contain"
                    />
                  </div>
                </div>
                <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
                  <p className="text-sm font-semibold text-primary uppercase tracking-wide mb-3">
                    Why Parents Love It
                  </p>
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    One hub that actually keeps the whole family in sync.
                  </h2>
                  <div className="space-y-4">
                    {[
                      'Local, accurate listings you can trust',
                      'All your kids’ schedules in one view',
                      'Works beautifully on any device',
                      'Built by parents who’ve been there'
                    ].map((item, index) => (
                      <div key={item} className="flex items-start gap-3">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white text-sm font-semibold">
                          {index + 1}
                        </div>
                        <p className="text-lg text-gray-800 leading-relaxed">
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
              FAQs
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq) => (
                <div key={faq.question} className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 text-base leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-primary to-purple-600 rounded-2xl p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Simplify the Season?
            </h2>
            <p className="text-xl mb-4">
              Stop juggling tabs and to-do lists.
            </p>
            <p className="text-xl mb-8">
              Start finding, syncing, and tracking with CampWhiz.
            </p>
            <Link 
              to="/register?type=parent"
              onClick={() => {
                trackEvent('cta_clicked', {
                  cta_location: 'for_parents_hero',
                  cta_text: 'Start FREE',
                  destination: 'register',
                  user_type: 'parent',
                  page: 'for_parents'
                });
                track('cta_clicked', {
                  persona: 'parent',
                  page: 'for_parents',
                  cta_location: 'for_parents_hero',
                  cta_text: 'Start FREE',
                  destination: 'register',
                });
              }}
              className="inline-block bg-white text-primary px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors"
            >
              Start FREE →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

